import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";
import { PublicLayout } from "../components/public/PublicLayout";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";

const COUNTRIES = ["Portugal","Spain","France","Ireland","United Kingdom","Germany","Italy","Netherlands","Belgium","Luxembourg","Austria","Poland","Czechia","Slovakia","Slovenia","Croatia","Hungary","Romania","Bulgaria","Greece","Cyprus","Malta","Denmark","Sweden","Finland","Norway","Iceland","Switzerland","Liechtenstein","Estonia","Latvia","Lithuania","Albania","Bosnia and Herzegovina","Montenegro","North Macedonia","Serbia","Moldova","Ukraine","Andorra","Monaco","San Marino"];

export default function Partner() {
  const { t } = useLang();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    full_name: "", business_type: "Retail", company_name: "", country: "Portugal",
    vat_id: "", address: "", postcode: "", phone: "", email: "", message: "", gdpr_consent: false,
  });
  useEffect(() => { document.title = "Become a Partner | Sambiva Trade"; }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.gdpr_consent) { toast.error("Please accept the consent statement to continue."); return; }
    setLoading(true);
    try {
      await api.post("/partner-applications", form);
      setSubmitted(true);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full px-4 py-3 rounded-xl border border-sambiva-charcoal/15 bg-white focus:outline-none focus:ring-2 focus:ring-sambiva-red/40 text-sm";
  const label = "block text-sm font-medium text-sambiva-charcoal mb-1.5";

  if (submitted) {
    return (
      <PublicLayout>
        <div className="max-w-xl mx-auto px-4 sm:px-6 py-28 text-center" data-testid="partner-success">
          <CheckCircle2 className="mx-auto text-sambiva-green mb-5" size={56} />
          <h1 className="font-serif text-3xl text-sambiva-charcoal mb-4">Application received</h1>
          <p className="text-sambiva-muted text-lg">{t("partner.success")}</p>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <section className="bg-sambiva-red text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-20 text-center">
          <h1 className="font-serif text-4xl sm:text-5xl mb-4">{t("partner.title")}</h1>
          <p className="text-white/90 text-lg max-w-2xl mx-auto">{t("partner.sub")}</p>
        </div>
      </section>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-14">
        <form onSubmit={submit} data-testid="partner-form" className="bg-white rounded-3xl p-6 sm:p-8 border border-sambiva-charcoal/5 space-y-5">
          <div>
            <label className={label}>{t("partner.fullName")} *</label>
            <input data-testid="pf-full-name" required value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className={field} />
          </div>
          <div>
            <label className={label}>{t("partner.businessType")} *</label>
            <select data-testid="pf-business-type" value={form.business_type} onChange={(e) => set("business_type", e.target.value)} className={field}>
              <option value="Retail">Retail</option>
              <option value="Online">Online</option>
              <option value="Restaurant">Restaurant</option>
            </select>
          </div>
          <div>
            <label className={label}>{t("partner.companyName")} *</label>
            <input data-testid="pf-company" required value={form.company_name} onChange={(e) => set("company_name", e.target.value)} className={field} />
          </div>
          <div>
            <label className={label}>{t("partner.country")} *</label>
            <select data-testid="pf-country" value={form.country} onChange={(e) => set("country", e.target.value)} className={field}>
              {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className={label}>{t("partner.vat")} *</label>
              <input data-testid="pf-vat" required value={form.vat_id} onChange={(e) => set("vat_id", e.target.value)} className={field} />
            </div>
            <div>
              <label className={label}>{t("partner.phone")} *</label>
              <input data-testid="pf-phone" required value={form.phone} onChange={(e) => set("phone", e.target.value)} className={field} />
            </div>
          </div>
          <div>
            <label className={label}>{t("partner.address")} *</label>
            <input data-testid="pf-address" required value={form.address} onChange={(e) => set("address", e.target.value)} className={field} />
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className={label}>{t("partner.postcode")} *</label>
              <input data-testid="pf-postcode" required value={form.postcode} onChange={(e) => set("postcode", e.target.value)} className={field} />
            </div>
            <div>
              <label className={label}>{t("common.email")} *</label>
              <input data-testid="pf-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} className={field} />
            </div>
          </div>
          <div>
            <label className={label}>{t("partner.message")}</label>
            <textarea data-testid="pf-message" rows={4} value={form.message} onChange={(e) => set("message", e.target.value)} placeholder={t("partner.messagePlaceholder")} className={field} />
          </div>
          <label className="flex items-start gap-3 cursor-pointer">
            <input data-testid="pf-gdpr" type="checkbox" checked={form.gdpr_consent} onChange={(e) => set("gdpr_consent", e.target.checked)} className="mt-1 w-4 h-4 accent-[#BE2026]" />
            <span className="text-sm text-sambiva-muted">{t("partner.gdpr")}</span>
          </label>
          <button data-testid="pf-submit" disabled={loading} type="submit" className="w-full bg-sambiva-red hover:bg-sambiva-deepred text-white font-semibold py-3.5 rounded-full transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
            {loading && <Loader2 className="animate-spin" size={18} />} {t("partner.submit")}
          </button>
        </form>
      </div>
    </PublicLayout>
  );
}
